/** Chat alerts stay on the envelope icon — exclude from bell / Notifications page. */
import type { UserConnectionResponse } from "../types/Types";
import { resolveProfessionalPhotoUrl } from "./profilePhoto";

export function isMessageNotification(item: { name?: string }): boolean {
  return (item.name || "").trim().toLowerCase() === "new message";
}

export const SYSTEM_NOTIFICATION_AVATAR = "/smbf-logo-sm.png";
export const DEFAULT_USER_NOTIFICATION_AVATAR = "/user.png";

/** Connection alerts are from another member; everything else is system/admin. */
export function isUserOriginatedNotification(notification: {
  description?: string;
}): boolean {
  const description = (notification.description || "").trim().toLowerCase();
  return (
    description.includes("connection request") ||
    description.includes("accepted your connection") ||
    description.includes("rejected your connection")
  );
}

/** Where connection / event / admin notifications should go on click. */
export function resolveNotificationPath(notification: {
  id: number;
  name?: string;
  description?: string;
}): string {
  const name = (notification.name || "").trim().toLowerCase();

  if (isUserOriginatedNotification(notification)) {
    return "/connections";
  }

  if (name === "new event") {
    return "/event-booking";
  }

  return `/notifications?id=${notification.id}`;
}

export function isNavigableSystemNotification(notification: {
  name?: string;
  description?: string;
}): boolean {
  const path = resolveNotificationPath({ ...notification, id: 0 });
  return path === "/connections" || path === "/event-booking";
}

export function collectNotificationAvatarsByName(
  ...sources: Array<UserConnectionResponse | undefined>
): Map<string, string> {
  const map = new Map<string, string>();

  for (const source of sources) {
    if (!source?.userDtos?.length) continue;

    for (const member of source.userDtos) {
      const fullName = `${member.firstName || ""} ${member.lastName || ""}`
        .trim()
        .toLowerCase();
      if (!fullName || map.has(fullName)) continue;

      const images = source.userImageUrls?.find(
        (entry) => entry.userId === member.id
      )?.imageUrls;
      const url = resolveProfessionalPhotoUrl({
        ...member,
        ...(images || {}),
      });
      if (url) map.set(fullName, url);
    }
  }

  return map;
}

export function resolveNotificationAvatarUrl(
  notification: { name?: string; description?: string },
  avatarByName: Map<string, string>
): string {
  if (!isUserOriginatedNotification(notification)) {
    return SYSTEM_NOTIFICATION_AVATAR;
  }

  const key = (notification.name || "").trim().toLowerCase();
  if (!key) return DEFAULT_USER_NOTIFICATION_AVATAR;
  return avatarByName.get(key) || DEFAULT_USER_NOTIFICATION_AVATAR;
}
